import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { BiPlus, BiTrash } from "react-icons/bi";
import axiosClient from "../utils/apiClient.js";

import { API_BASE_URL } from '../utils/urls.js';

const AgentTimeEntryList = () => {
  const [timeEntries, setTimeEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const user = JSON.parse(localStorage.getItem("user"));
  const isAdmin = user?.roleName === "admin";
  const isCaseManager = user?.roleName === "case_manager";
  const isFieldAgent = user?.roleName === "field_agent";

  const canViewAllEntries = isAdmin || isCaseManager;
  const canDelete = isAdmin || isCaseManager || isFieldAgent;

  useEffect(() => {
    const fetchTimeEntries = async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        setError("Authentication token not found.");
        setLoading(false);
        return;
      }

      try {
        let response;
        if (canViewAllEntries) {
          // Admins and Case Managers can see all entries
          response = await axiosClient.get(`${API_BASE_URL}/time-entries/all`);
        } else if (isFieldAgent) {
          // Field Agents can only see their own entries
          response = await axiosClient.get(
            `${API_BASE_URL}/time-entries/user/${user.id}`  
          );
        } else {
          setError("You don't have permission to view this page.");
          setLoading(false);
          return;
        }
        setTimeEntries(response.data);
      } catch (err) {
        console.error("Error fetching time entries:", err);
        setError(
          err.response?.data?.message ||
            "Failed to load time entries. Please try again."
        );
      } finally {
        setLoading(false);
      }
    };

    fetchTimeEntries();
  }, [user?.id, canViewAllEntries, isFieldAgent]);

  const handleDelete = async (entryId) => {
    if (window.confirm("Are you sure you want to delete this time entry?")) {
      const token = localStorage.getItem("token");
      try {
        await axios.delete(`${API_BASE_URL}/time-entries/${entryId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setTimeEntries(timeEntries.filter((entry) => entry.id !== entryId));
      } catch (err) {
        console.error("Error deleting time entry:", err);
        setError(err.response?.data?.message || "Failed to delete time entry.");
      }
    }
  };

  if (loading) {
    return (
      <div className="p-6 text-center text-gray-500">
        Loading time entries...
      </div>
    );
  }

  if (error) {
    return <div className="p-6 text-center text-red-500">{error}</div>;
  }

  const totalHours = timeEntries.reduce(
    (sum, entry) => sum + (parseFloat(entry.hours) || 0),
    0
  );

  const totalExpenses = timeEntries.reduce(
    (sum, entry) => sum + (parseFloat(entry.expense_amount) || 0),
    0
  );

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-800">Time & Expenses</h1>
        {(isAdmin || isCaseManager || isFieldAgent) && (
          <Link
            to="/agent/time-entries/new"
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg shadow-md hover:bg-blue-700 transition"
          >
            <BiPlus className="mr-2" /> Log New Entry
          </Link>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        <div className="bg-white p-6 rounded-lg shadow-sm">
          <p className="text-gray-500 text-sm">Total Hours</p>
          <p className="text-2xl font-semibold text-gray-900">
            {totalHours.toFixed(2)}
          </p>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm">
          <p className="text-gray-500 text-sm">Total Expenses</p>
          <p className="text-2xl font-semibold text-gray-900">
            ${totalExpenses.toFixed(2)}
          </p>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Date
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Case
              </th>
              {canViewAllEntries && (
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Field Agent
                </th>
              )}
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Hours
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Expenses
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Description
              </th>
              {canDelete && (
                <th scope="col" className="relative px-6 py-3">
                  <span className="sr-only">Actions</span>
                </th>
              )}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {timeEntries.length > 0 ? (
              timeEntries.map((entry) => (
                <tr
                  key={entry.id}
                  className="hover:bg-gray-50 transition-colors duration-200"
                >
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {new Date(entry.entry_date).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {entry.case_name}
                  </td>
                  {canViewAllEntries && (
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {entry.first_name} {entry.last_name}
                    </td>
                  )}
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {entry.hours}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    ${parseFloat(entry.expense_amount).toFixed(2)}
                  </td>
                  <td className="px-6 py-4 whitespace-normal text-sm text-gray-500">
                    {entry.description}
                  </td>
                  {canDelete && (
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleDelete(entry.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <BiTrash className="inline-block h-5 w-5" />
                      </button>
                    </td>
                  )}
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={canViewAllEntries ? "7" : "6"}
                  className="px-6 py-4 text-center text-sm text-gray-500"
                >
                  No time entries found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AgentTimeEntryList;
